import type { ReactNode } from 'react';

import { PageHeader } from '@/components/PageHeader';
import { useIsMobile } from '@/lib/utils/useIsMobile';

/**
 * <b>모바일에서는 열지 않는 화면</b>의 껍데기 (사용자 지시 2026-08-31).
 *
 * ## 왜 「반응형으로 잘 만들기」 가 아니라 막는가
 * 증명서 서식·설문 양식처럼 <b>판을 짜는</b> 화면이 있다. 칸을 끌어 놓고, 좌표를 맞추고, 미리보기와
 * 견주며 고친다. 이런 일은 좁은 화면에서 «되긴 되는» 상태로 만들어 두면 <b>더 나쁘다</b> — 사람은
 * 열리니까 손을 대고, 손이 미끄러져 어긋난 서식이 저장되면 그것으로 발급된 증명서가 전부 어긋난다.
 * 못 여는 것은 불편이지만, 어긋난 채 저장되는 것은 사고다.
 *
 * ## 막되 <b>왜</b> 를 말한다
 * 빈 화면이나 「지원하지 않습니다」 한 줄로 끝내면 고장으로 읽힌다. 무엇을 하는 화면이고 어디서
 * 열어야 하는지를 적는다 — 그 사람은 자리에 돌아가서 다시 열 것이다.
 *
 * ## 기준선은 767px
 * {@link useIsMobile} 과 같은 값이다. 아이패드 세로(768)는 <b>연다</b> — 그 폭이면 판을 짜는 일이
 * 실제로 된다. 모달 버튼 자리를 가르는 선과 같은 선이라 둘이 따로 놀지 않는다.
 */
export function DesktopOnly({
  title,
  description,
  children,
}: {
  title: string;
  /** 이 화면이 무엇을 하는 곳인지. 없으면 제목만 나온다. */
  description?: string;
  children: ReactNode;
}) {
  const isMobile = useIsMobile();

  if (!isMobile) return <>{children}</>;

  return (
    <div className="space-y-4">
      <PageHeader title={title} description={description} />
      <div className="card p-5 space-y-2">
        <p style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>
          이 화면은 PC 에서 열어 주세요
        </p>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: 1.7 }}>
          칸을 끌어 놓고 미리보기와 견주며 고치는 화면이라 좁은 화면에서는 자리가 어긋납니다.
          어긋난 채로 저장하면 <b>그 서식으로 나가는 문서가 전부 함께 어긋납니다.</b>
        </p>
        <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
          가로 768px 이상이면 열립니다 — 태블릿을 가로로 돌리셔도 됩니다.
        </p>
      </div>
    </div>
  );
}
